import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Settings, History, RefreshCw, Download, 
  AlertTriangle, CheckCircle, Loader2, Package, Eye, Trash2,
  GitCompare, Plus, X, ArrowLeftRight, Database, Upload, FileSpreadsheet,
  Check, Square, CheckSquare, ClipboardCheck, Send
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';
import PresentacionesTab from './f8126/PresentacionesTab';

export default function DashboardF8126() {
  const [activeTab, setActiveTab] = useState('generar');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [presMap, setPresMap] = useState({});
  const [presBusy, setPresBusy] = useState('');
  const [taBusy, setTaBusy] = useState(false);
  const [resumen, setResumen] = useState({ periodos: [] });
  
  // Parámetros
  const [periodo, setPeriodo] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [umbralTransacciones, setUmbralTransacciones] = useState(1400000);
  const [umbralSaldo, setUmbralSaldo] = useState(400000);
  const [secuencia, setSecuencia] = useState(1);
  const [generarSinMovimientos, setGenerarSinMovimientos] = useState(false);
  const [modoUmbral, setModoUmbral] = useState('OR'); // OR, AND, TRANSACCIONES, SALDO
  
  // Filtro de CVU
  const [filtroCvuPrefijo, setFiltroCvuPrefijo] = useState('');
  const [filtroCvuModo, setFiltroCvuModo] = useState('INCLUIR'); // INCLUIR o EXCLUIR

  // Versión del reporte (V200 / V300) y exclusiones del preview
  const [version, setVersion] = useState('00200'); // '00200' (V200) o '00300' (V300)
  const [excludedCuits, setExcludedCuits] = useState(new Set()); // titulares quitados con la X
  const [paisOverrides, setPaisOverrides] = useState({}); // {cuit: {nacionalidad, pais}} para completar manual (V300)
  const [umbralReg06, setUmbralReg06] = useState(2500000); // V300: monto mínimo de transacción para listar en Reg 06
  
  // Config
  const [config, setConfig] = useState({
    cuit_informante: '33718330179',
    denominacion_informante: 'WAYA S.A.',
    codigo_concepto: '812',
    version_aplicativo: '00200'
  });
  
  // Múltiples períodos
  const [periodosMultiples, setPeriodosMultiples] = useState('');
  
  // Comparativa
  const [escenarios, setEscenarios] = useState([
    { nombre: 'Escenario A', periodo: periodo, umbral_transacciones: 1400000, umbral_saldo: 400000 },
    { nombre: 'Escenario B', periodo: periodo, umbral_transacciones: 1400000, umbral_saldo: 400000 }
  ]);
  const [comparativaResult, setComparativaResult] = useState(null);
  const [loadingComparativa, setLoadingComparativa] = useState(false);
  
  // Escenarios acumulados para el informe
  const [escenariosAcumulados, setEscenariosAcumulados] = useState([]);
  const [escenariosSeleccionados, setEscenariosSeleccionados] = useState(new Set());

  useEffect(() => {
    loadConfig();
    loadHistorial();
    loadResumen();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/reporte-f8126/config');
      setConfig(res.data);
      // Cargar filtro de CVU si existe
      if (res.data.filtro_cvu_prefijo !== undefined) {
        setFiltroCvuPrefijo(res.data.filtro_cvu_prefijo || '');
      }
      if (res.data.filtro_cvu_modo) {
        setFiltroCvuModo(res.data.filtro_cvu_modo);
      }
    } catch (error) {
      console.error('Error loading config');
    }
  };

  const loadHistorial = async () => {
    try {
      const res = await api.get('/reporte-f8126/historial?limit=50');
      setHistorial(res.data);
      loadPresentaciones();
    } catch (error) {
      console.error('Error loading historial');
    }
  };

  const loadPresentaciones = async () => {
    try {
      const res = await api.get('/arca-presentaciones?tipo=8126');
      const map = {};
      (res.data.rows || []).forEach((p) => { if (!map[p.reporte_id]) map[p.reporte_id] = p; });
      setPresMap(map);
    } catch (e) { /* silencioso */ }
  };

  const presentarArca = async (rep) => {
    setPresBusy(rep.id);
    try {
      const res = await api.post('/arca-presentaciones/presentar', { tipo: '8126', reporte_id: rep.id });
      if (res.data.ok === false) {
        toast.error(res.data.error || 'No se pudo presentar en ARCA');
      } else {
        toast.success(`Presentado en ARCA. Nº transacción: ${res.data.numeroTransaccion || 's/d'}`);
      }
      loadPresentaciones();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'No se pudo presentar en ARCA');
      loadPresentaciones();
    } finally { setPresBusy(''); }
  };

  const consultarArca = async (presId) => {
    setPresBusy(presId);
    try {
      const res = await api.post('/arca-presentaciones/consultar', { presentacion_id: presId });
      if (res.data.ok === false) {
        toast.error(res.data.error || 'No se pudo consultar el estado');
      } else {
        const est = res.data.estado;
        toast.success(est === 'PRESENTADA' ? `Presentada (Nº ${res.data.numeroTransaccion})`
          : est === 'PENDIENTE' ? 'Aún en proceso en ARCA' : 'No encontrada en ARCA');
      }
      loadPresentaciones();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'No se pudo consultar el estado');
    } finally { setPresBusy(''); }
  };

  const forzarTA = async () => {
    setTaBusy(true);
    try {
      const res = await api.post('/arca-presentaciones/forzar-ta');
      toast.success(res.data?.mensaje || 'Token WSAA renovado. Ya podés reintentar la presentación.');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || 'No se pudo renovar el token');
    } finally { setTaBusy(false); }
  };

  const loadResumen = async () => {
    try {
      const res = await api.get('/reporte-f8126/resumen');
      setResumen(res.data);
    } catch (error) {
      console.error('Error loading resumen');
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      // Asegurar que los valores son números válidos
      const umbralTrans = Number(umbralTransacciones) || 0;
      const umbralSal = Number(umbralSaldo) || 0;
      
      
      const res = await api.post('/reporte-f8126/preview', {
        periodo,
        umbral_transacciones: umbralTrans,
        umbral_saldo: umbralSal,
        modo_umbral: modoUmbral,
        filtro_cvu_prefijo: filtroCvuPrefijo || null,
        filtro_cvu_modo: filtroCvuModo,
        version
      });
      setPreview(res.data);
      // Reiniciar exclusiones y overrides al generar un nuevo preview
      setExcludedCuits(new Set());
      // Pre-cargar overrides para titulares que requieren completar país (V300)
      const initOverrides = {};
      if (version === '00300') {
        (res.data.cuentas || []).forEach((c) => {
          if (c.needs_review) {
            initOverrides[c.cuit] = { nacionalidad: c.nacionalidad || 'E', pais: c.pais_codigo || '' };
          }
        });
      }
      setPaisOverrides(initOverrides);
      const reviewMsg = res.data.total_review ? ` · ${res.data.total_review} requieren completar país` : '';
      toast.success(`${res.data.total_cuentas} titulares encontrados${reviewMsg}`);
    } catch (error) {
      toast.error('Error al generar preview');
      console.error('Error preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerar = async () => {
    setLoading(true);
    try {
      // Intentar primero con generación síncrona (más confiable para archivos medianos)
      toast.info('Generando reporte... Por favor espere.');
      
      try {
        const res = await api.post('/reporte-f8126/generar', {
          periodo,
          umbral_transacciones: umbralTransacciones,
          umbral_saldo: umbralSaldo,
          secuencia,
          generar_sin_movimientos: generarSinMovimientos,
          modo_umbral: modoUmbral,
          filtro_cvu_prefijo: filtroCvuPrefijo || null,
          filtro_cvu_modo: filtroCvuModo,
          version,
          excluded_cuits: Array.from(excludedCuits),
          pais_overrides: paisOverrides,
          umbral_transaccion_reg06: umbralReg06
        });
        
        toast.success(`Reporte generado: ${res.data.nombre_archivo}`);
        loadHistorial();
        loadResumen();
        handleDescargar(res.data.id, res.data.nombre_archivo);
        setLoading(false);
        return;
      } catch (syncError) {
        // Si falla el síncrono (timeout), intentar asíncrono
      }
      
      // Fallback: Usar generación asíncrona
      const initRes = await api.post('/reporte-f8126/generar-async', {
        periodo,
        umbral_transacciones: umbralTransacciones,
        umbral_saldo: umbralSaldo,
        secuencia,
        generar_sin_movimientos: generarSinMovimientos,
        modo_umbral: modoUmbral,
        filtro_cvu_prefijo: filtroCvuPrefijo || null,
        filtro_cvu_modo: filtroCvuModo,
        version,
        excluded_cuits: Array.from(excludedCuits),
        pais_overrides: paisOverrides,
        umbral_transaccion_reg06: umbralReg06
      });
      
      const jobId = initRes.data.job_id;
      toast.info('Procesando en segundo plano...');
      
      // Polling del estado cada 3 segundos
      let intentos = 0;
      const maxIntentos = 100;
      let erroresConsecutivos = 0;
      
      const checkStatus = async () => {
        try {
          const statusRes = await api.get(`/reporte-f8126/job-status/${jobId}`);
          const status = statusRes.data;
          erroresConsecutivos = 0;
          
          if (status.estado === 'completado') {
            toast.success(`Reporte generado: ${status.resultado?.nombre_archivo || 'Completado'}`);
            loadHistorial();
            loadResumen();
            if (status.resultado?.id) {
              handleDescargar(status.resultado.id, status.resultado.nombre_archivo);
            }
            setLoading(false);
            return;
          } else if (status.estado === 'error') {
            toast.error(status.mensaje || 'Error al generar reporte');
            console.error('Error en job:', status.error);
            setLoading(false);
            return;
          }
          
          intentos++;
          if (intentos < maxIntentos) {
            setTimeout(checkStatus, 3000);
          } else {
            toast.error('Tiempo de espera agotado. Revise el historial.');
            loadHistorial();
            setLoading(false);
          }
        } catch (err) {
          erroresConsecutivos++;
          console.error('Error verificando estado:', err);
          
          if (erroresConsecutivos < 3) {
            intentos++;
            if (intentos < maxIntentos) {
              setTimeout(checkStatus, 3000);
            }
          } else {
            toast.error('Error de conexión. Revise el historial para ver si el reporte se generó.');
            loadHistorial();
            setLoading(false);
          }
        }
      };
      
      setTimeout(checkStatus, 2000);
      
    } catch (error) {
      console.error('Error en generación:', error);
      toast.error(error.response?.data?.detail || 'Error al generar reporte');
      setLoading(false);
    }
  };

  const toggleExcludeCuit = (cuit) => {
    setExcludedCuits((prev) => {
      const next = new Set(prev);
      if (next.has(cuit)) next.delete(cuit);
      else next.add(cuit);
      return next;
    });
  };

  const updatePaisOverride = (cuit, field, value) => {
    setPaisOverrides((prev) => ({
      ...prev,
      [cuit]: { ...(prev[cuit] || {}), [field]: value }
    }));
  };

  const handleGenerarMultiple = async () => {
    const periodos = periodosMultiples.split(',').map(p => p.trim()).filter(p => p.length === 6);
    if (periodos.length === 0) {
      toast.error('Ingrese períodos válidos (YYYYMM) separados por coma');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/reporte-f8126/generar-multiple', {
        periodos,
        umbral_transacciones: umbralTransacciones,
        umbral_saldo: umbralSaldo,
        secuencia,
        generar_sin_movimientos: generarSinMovimientos,
        modo_umbral: modoUmbral,
        filtro_cvu_prefijo: filtroCvuPrefijo || null,
        filtro_cvu_modo: filtroCvuModo
      });
      
      toast.success(`${res.data.reportes_generados} reportes generados`);
      
      if (res.data.errores?.length > 0) {
        toast.warning(`${res.data.errores.length} períodos con errores`);
      }
      
      // Descargar ZIP usando fetch autenticado
      if (res.data.zip_id) {
        try {
          const zipRes = await api.get(`/reporte-f8126/descargar-zip/${res.data.zip_id}`, {
            responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([zipRes.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `F8126_multiple_${res.data.zip_id.slice(0, 8)}.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (zipError) {
          toast.error('Error al descargar ZIP. Intente desde el historial.');
          console.error('Error descarga ZIP:', zipError);
        }
      }
      
      loadHistorial();
      loadResumen();
    } catch (error) {
      toast.error('Error al generar reportes');
    } finally {
      setLoading(false);
    }
  };

  const handleDescargar = async (id, nombre) => {
    try {
      toast.info('Preparando descarga...');
      
      // Obtener URL de descarga directa (para archivos grandes)
      const res = await api.get(`/reporte-f8126/descargar-url/${id}`);
      const downloadUrl = res.data.download_url;
      
      // Abrir en nueva ventana para descarga directa
      window.open(downloadUrl, '_blank');
      
      toast.success('Descarga iniciada');
    } catch (error) {
      console.error('Error al preparar descarga:', error);
      // Fallback al método tradicional
      try {
        const res = await api.get(`/reporte-f8126/descargar/${id}`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', nombre || `reporte_${id}.txt`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (fallbackError) {
        toast.error('Error al descargar archivo');
      }
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este reporte?')) return;
    
    try {
      await api.delete(`/reporte-f8126/reporte/${id}`);
      toast.success('Reporte eliminado');
      loadHistorial();
      loadResumen();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await api.post('/reporte-f8126/config', {
        ...config,
        filtro_cvu_prefijo: filtroCvuPrefijo,
        filtro_cvu_modo: filtroCvuModo
      });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const handleSincronizarPadron = async () => {
    if (!window.confirm('¿Sincronizar el padrón desde MySQL? Esto puede tomar unos segundos.')) return;
    
    setLoading(true);
    try {
      const res = await api.post('/padron-interno/sincronizar-desde-mysql');
      const data = res.data;
      toast.success(
        `Sincronización completada: ${data.totales.documentos_padron_interno} CUITs, ${data.totales.cvus_totales} CVUs`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar padrón');
    } finally {
      setLoading(false);
    }
  };

  const handleExportarPadron = async () => {
    setLoading(true);
    try {
      const res = await api.get('/padron-interno/exportar-json', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'padron_interno_export.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Padrón exportado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar padrón');
    } finally {
      setLoading(false);
    }
  };

  const handleImportarPadron = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!window.confirm(`¿Importar padrón desde "${file.name}"? Se agregarán los CVUs faltantes sin eliminar datos existentes.`)) {
      e.target.value = '';
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/padron-interno/importar-json', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const data = res.data;
      const stats = data.stats;
      
      toast.success(
        `Importación completada: ${stats.cuits_creados} CUITs nuevos, ${stats.cvus_agregados} CVUs agregados. ` +
        `Total: ${data.totales_finales.documentos_padron_interno} CUITs, ${data.totales_finales.cvus_totales} CVUs`
      );
      
      if (stats.errores?.length > 0) {
        console.warn('Errores durante importación:', stats.errores);
        toast.warning(`${stats.errores.length} errores durante la importación. Ver consola.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar padrón');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(monto);
  };

  // Funciones de comparativa
  const agregarEscenario = () => {
    if (escenarios.length >= 5) {
      toast.error('Máximo 5 escenarios permitidos');
      return;
    }
    const letras = ['A', 'B', 'C', 'D', 'E'];
    setEscenarios([
      ...escenarios,
      { 
        nombre: `Escenario ${letras[escenarios.length]}`, 
        periodo: periodo, 
        umbral_transacciones: umbralTransacciones, 
        umbral_saldo: umbralSaldo 
      }
    ]);
  };

  const eliminarEscenario = (index) => {
    if (escenarios.length <= 2) {
      toast.error('Se requieren al menos 2 escenarios');
      return;
    }
    setEscenarios(escenarios.filter((_, i) => i !== index));
  };

  const actualizarEscenario = (index, campo, valor) => {
    const nuevos = [...escenarios];
    nuevos[index] = { ...nuevos[index], [campo]: valor };
    setEscenarios(nuevos);
  };

  const handleComparativa = async () => {
    setLoadingComparativa(true);
    setComparativaResult(null);
    try {
      const res = await api.post('/reporte-f8126/comparativa', { escenarios });
      setComparativaResult(res.data);
      toast.success('Comparativa generada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar comparativa');
    } finally {
      setLoadingComparativa(false);
    }
  };

  const handleDescargarComparativaPDF = async () => {
    setLoadingComparativa(true);
    try {
      const response = await api.post('/reporte-f8126/comparativa/pdf', { escenarios }, {
        responseType: 'blob'
      });
      
      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comparativa_F8126_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar PDF');
    } finally {
      setLoadingComparativa(false);
    }
  };

  // Funciones para escenarios acumulados
  const toggleSeleccionEscenario = (escenarioKey) => {
    const nuevaSeleccion = new Set(escenariosSeleccionados);
    if (nuevaSeleccion.has(escenarioKey)) {
      nuevaSeleccion.delete(escenarioKey);
    } else {
      nuevaSeleccion.add(escenarioKey);
    }
    setEscenariosSeleccionados(nuevaSeleccion);
  };

  const agregarAlInforme = () => {
    
    if (!comparativaResult || !comparativaResult.escenarios) {
      toast.error('Primero genera una comparativa');
      return;
    }
    
    const seleccionados = Array.from(escenariosSeleccionados);
    if (seleccionados.length === 0) {
      toast.error('Selecciona al menos un escenario');
      return;
    }

    const nuevosEscenarios = [];
    for (const nombreEscenario of seleccionados) {
      // Buscar el escenario por nombre en el array de escenarios
      const escenarioData = comparativaResult.escenarios.find(e => e.nombre === nombreEscenario);
      const resumenData = comparativaResult.resumen_diferencias?.por_escenario?.[nombreEscenario] || {};
      
      if (escenarioData) {
        // Verificar que no esté duplicado
        const yaExiste = escenariosAcumulados.some(
          e => e.nombre === nombreEscenario && e.periodo === escenarioData.periodo
        );
        if (!yaExiste) {
          nuevosEscenarios.push({
            nombre: nombreEscenario,
            periodo: escenarioData.periodo,
            umbral_transacciones: escenarioData.umbral_transacciones,
            umbral_saldo: escenarioData.umbral_saldo,
            total_cuentas: escenarioData.totales?.total_cuentas || 0,
            cuentas_exclusivas: resumenData.cuentas_exclusivas || 0,
            total_movimientos: escenarioData.totales?.total_movimientos_sum || 0,
            total_ingresos: escenarioData.totales?.total_ingresos_sum || 0,
            total_egresos: escenarioData.totales?.total_egresos_sum || 0,
            lineas_archivo: escenarioData.lineas_archivo || 0,
            cuentas: escenarioData.cuentas || []
          });
        }
      }
    }

    
    if (nuevosEscenarios.length > 0) {
      setEscenariosAcumulados([...escenariosAcumulados, ...nuevosEscenarios]);
      toast.success(`${nuevosEscenarios.length} escenario(s) agregado(s) al informe`);
      setEscenariosSeleccionados(new Set());
    } else {
      toast.info('Los escenarios seleccionados ya están en el informe');
    }
  };

  const eliminarDelInforme = (index) => {
    setEscenariosAcumulados(escenariosAcumulados.filter((_, i) => i !== index));
  };

  const limpiarInforme = () => {
    if (window.confirm('¿Limpiar todos los escenarios del informe?')) {
      setEscenariosAcumulados([]);
      toast.success('Informe limpiado');
    }
  };

  const exportarInformeExcel = async () => {
    
    let datosExportar = [];
    
    if (escenariosAcumulados.length > 0) {
      datosExportar = escenariosAcumulados;
    } else if (comparativaResult && comparativaResult.escenarios) {
      datosExportar = comparativaResult.escenarios.map(esc => {
        const resumen = comparativaResult.resumen_diferencias?.por_escenario?.[esc.nombre] || {};
        return {
          nombre: esc.nombre,
          periodo: esc.periodo,
          umbral_transacciones: esc.umbral_transacciones,
          umbral_saldo: esc.umbral_saldo,
          total_cuentas: esc.totales?.total_cuentas || 0,
          cuentas_exclusivas: resumen.cuentas_exclusivas || 0,
          total_movimientos: esc.totales?.total_movimientos_sum || 0,
          total_ingresos: esc.totales?.total_ingresos_sum || 0,
          total_egresos: esc.totales?.total_egresos_sum || 0
        };
      });
    }

    
    if (datosExportar.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    try {
      // Crear datos para Excel
      const excelData = {
        escenarios: datosExportar.map(esc => ({
          nombre: esc.nombre,
          periodo: esc.periodo,
          umbral_transacciones: esc.umbral_transacciones,
          umbral_saldo: esc.umbral_saldo,
          total_cuentas: esc.total_cuentas,
          cuentas_exclusivas: esc.cuentas_exclusivas,
          total_movimientos: esc.total_movimientos,
          total_ingresos: esc.total_ingresos || 0,
          total_egresos: esc.total_egresos || 0
        }))
      };


      const response = await api.post('/reporte-f8126/comparativa/excel', excelData, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Comparativa_F8126_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al exportar Excel');
    }
  };

  const exportarInformePDF = async () => {
    
    let datosExportar = [];
    
    if (escenariosAcumulados.length > 0) {
      datosExportar = escenariosAcumulados;
    } else if (comparativaResult && comparativaResult.escenarios) {
      datosExportar = comparativaResult.escenarios.map(esc => {
        const resumen = comparativaResult.resumen_diferencias?.por_escenario?.[esc.nombre] || {};
        return {
          nombre: esc.nombre,
          periodo: esc.periodo,
          umbral_transacciones: esc.umbral_transacciones,
          umbral_saldo: esc.umbral_saldo,
          total_cuentas: esc.totales?.total_cuentas || 0,
          cuentas_exclusivas: resumen.cuentas_exclusivas || 0,
          total_movimientos: esc.totales?.total_movimientos_sum || 0,
          total_ingresos: esc.totales?.total_ingresos_sum || 0,
          total_egresos: esc.totales?.total_egresos_sum || 0
        };
      });
    }

    if (datosExportar.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    try {
      const pdfData = {
        escenarios: datosExportar.map(esc => ({
          nombre: esc.nombre,
          periodo: esc.periodo,
          umbral_transacciones: esc.umbral_transacciones,
          umbral_saldo: esc.umbral_saldo,
          total_cuentas: esc.total_cuentas,
          cuentas_exclusivas: esc.cuentas_exclusivas,
          total_movimientos: esc.total_movimientos,
          total_ingresos: esc.total_ingresos || 0,
          total_egresos: esc.total_egresos || 0
        }))
      };

      const response = await api.post('/reporte-f8126/informe/pdf', pdfData, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Informe_F8126_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al exportar PDF');
    }
  };

  const getColorEscenario = (index) => {
    const colores = [
      { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
      { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
      { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
      { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' }
    ];
    return colores[index % colores.length];
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reporte F8126</h1>
          <p className="text-slate-500">Régimen de información PSP - AFIP</p>
        </div>
        <Button variant="outline" onClick={() => { loadHistorial(); loadResumen(); }}>
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="generar" className="flex items-center gap-2">
            <FileText size={16} />
            Generar
          </TabsTrigger>
          <TabsTrigger value="comparativa" className="flex items-center gap-2">
            <GitCompare size={16} />
            Comparativa
          </TabsTrigger>
          <TabsTrigger value="multiple" className="flex items-center gap-2">
            <Package size={16} />
            Múltiples
          </TabsTrigger>
          <TabsTrigger value="historial" className="flex items-center gap-2">
            <History size={16} />
            Historial
          </TabsTrigger>
          <TabsTrigger value="presentaciones" className="flex items-center gap-2" data-testid="f8126-tab-presentaciones">
            <ClipboardCheck size={16} />
            Presentaciones
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings size={16} />
            Configuración
          </TabsTrigger>
        </TabsList>

        {/* Tab: Generar */}
        <TabsContent value="generar" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Parámetros */}
            <Card className="p-6 bg-white">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Settings size={18} />
                Parámetros
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Período (YYYYMM)</Label>
                  <Input 
                    value={periodo} 
                    onChange={(e) => setPeriodo(e.target.value)}
                    placeholder="202501"
                    maxLength={6}
                  />
                </div>

                <div>
                  <Label>Versión del reporte</Label>
                  <select
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="f8126-version-select"
                  >
                    <option value="00200">F8126 V200 (actual)</option>
                    <option value="00300">F8126 V300 (nueva)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {version === '00300'
                      ? 'Formato V300: nuevos registros (02/03/05/06), país por nacionalidad y CRLF.'
                      : 'Formato V200: layout original sin cambios.'}
                  </p>
                </div>

                {version === '00300' && (
                  <div data-testid="f8126-umbral-reg06-wrap">
                    <Label>Monto mínimo transacción (Reg 06)</Label>
                    <Input
                      type="number"
                      value={umbralReg06}
                      onChange={(e) => setUmbralReg06(Number(e.target.value) || 0)}
                      placeholder="2500000"
                      data-testid="f8126-umbral-reg06-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Solo se listan en el Reg 06 las transacciones con monto ≥ a este valor. Ingresos/egresos (Reg 05) y saldos se calculan completos.
                    </p>
                  </div>
                )}
                
                <div>
                  <Label>Umbral Transacciones</Label>
                  <Input 
                    type="number"
                    value={umbralTransacciones} 
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : Number(e.target.value);
                      setUmbralTransacciones(val);
                    }}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Incluir si mov. totales ≥ este monto
                  </p>
                </div>
                
                <div>
                  <Label>Umbral Saldo</Label>
                  <Input 
                    type="number"
                    value={umbralSaldo} 
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : Number(e.target.value);
                      setUmbralSaldo(val);
                    }}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Incluir si saldo ≥ este monto
                  </p>
                </div>
                
                <div>
                  <Label>Modo Evaluación Umbrales</Label>
                  <select
                    value={modoUmbral}
                    onChange={(e) => setModoUmbral(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="OR">Cualquiera (OR) - Transacciones o Saldo</option>
                    <option value="AND">Ambos (AND) - Transacciones y Saldo</option>
                    <option value="TRANSACCIONES">Solo Transacciones</option>
                    <option value="SALDO">Solo Saldo</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {modoUmbral === 'OR' && 'Incluir si cumple cualquiera de los umbrales'}
                    {modoUmbral === 'AND' && 'Incluir solo si cumple AMBOS umbrales'}
                    {modoUmbral === 'TRANSACCIONES' && 'Ignorar umbral de saldo'}
                    {modoUmbral === 'SALDO' && 'Ignorar umbral de transacciones'}
                  </p>
                </div>
                
                {/* Filtro de CVU */}
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Label className="text-amber-800 font-medium">Filtro por CVU</Label>
                  <div className="flex gap-2 mt-2">
                    <select
                      value={filtroCvuModo}
                      onChange={(e) => setFiltroCvuModo(e.target.value)}
                      className="px-3 py-2 border border-amber-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                    >
                      <option value="INCLUIR">Incluir solo</option>
                      <option value="EXCLUIR">Excluir</option>
                    </select>
                    <Input 
                      placeholder="Prefijo CVU (ej: 2000)"
                      value={filtroCvuPrefijo}
                      onChange={(e) => setFiltroCvuPrefijo(e.target.value)}
                      className="flex-1 border-amber-300 focus:ring-amber-500"
                    />
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    {filtroCvuPrefijo ? (
                      filtroCvuModo === 'INCLUIR' 
                        ? `Solo CVUs que empiecen con "${filtroCvuPrefijo}"` 
                        : `Excluir CVUs que empiecen con "${filtroCvuPrefijo}"`
                    ) : 'Dejar vacío para incluir todos los CVUs'}
                  </p>
                </div>
                
                <div>
                  <Label>Secuencia</Label>
                  <Input 
                    type="number"
                    value={secuencia} 
                    onChange={(e) => setSecuencia(Number(e.target.value))}
                    min={1}
                  />
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <input 
                    type="checkbox" 
                    id="sinMovimientos"
                    checked={generarSinMovimientos}
                    onChange={(e) => setGenerarSinMovimientos(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <Label htmlFor="sinMovimientos" className="text-sm cursor-pointer">
                    Generar sin movimientos (solo cabecera)
                  </Label>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handlePreview} 
                    variant="outline" 
                    disabled={loading}
                    className="flex-1"
                  >
                    {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Eye size={16} className="mr-2" />}
                    Preview
                  </Button>
                  <Button 
                    onClick={handleGenerar} 
                    disabled={loading || (!preview && !generarSinMovimientos)}
                    className="flex-1"
                    style={{ backgroundColor: '#1e40af' }}
                  >
                    {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Download size={16} className="mr-2" />}
                    Generar
                  </Button>
                </div>
              </div>
            </Card>

            {/* Preview */}
            <Card className="p-6 bg-white lg:col-span-2">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Eye size={18} />
                Vista Previa
              </h3>
              
              {!preview ? (
                <div className="text-center text-slate-500 py-12">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Haga clic en "Preview" para ver los datos</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Resumen */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600">CUITs</p>
                      <p className="text-xl font-bold text-blue-800">{preview.total_cuentas}</p>
                    </div>
                    <div className="p-3 bg-cyan-50 rounded-lg">
                      <p className="text-xs text-cyan-600">CVUs</p>
                      <p className="text-xl font-bold text-cyan-800">{preview.total_cvus || '-'}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-600">Total Ingresos</p>
                      <p className="text-lg font-bold text-green-800">{formatMonto(preview.total_ingresos_sum)}</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-xs text-red-600">Total Egresos</p>
                      <p className="text-lg font-bold text-red-800">{formatMonto(preview.total_egresos_sum)}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs text-purple-600">Secuencia</p>
                      <p className="text-xl font-bold text-purple-800">{preview.proxima_secuencia}</p>
                    </div>
                  </div>
                  
                  {/* Resumen de inclusión/exclusión */}
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <Badge variant="outline" className="bg-green-50 text-green-700" data-testid="f8126-incluidos-badge">
                      Incluidos: {(preview.total_cuentas || 0) - excludedCuits.size}
                    </Badge>
                    {excludedCuits.size > 0 && (
                      <Badge variant="outline" className="bg-red-50 text-red-700" data-testid="f8126-excluidos-badge">
                        Quitados: {excludedCuits.size}
                      </Badge>
                    )}
                    {version === '00300' && preview.total_review > 0 && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700">
                        Completar país: {preview.total_review}
                      </Badge>
                    )}
                  </div>

                  {/* Tabla de cuentas */}
                  <div className="max-h-[400px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-center">Quitar</TableHead>
                          <TableHead>Razón Social</TableHead>
                          <TableHead>CUIT</TableHead>
                          <TableHead className="text-center">CVUs</TableHead>
                          {version === '00300' && <TableHead className="text-center">Nac. / País</TableHead>}
                          <TableHead className="text-right">Ingresos</TableHead>
                          <TableHead className="text-right">Egresos</TableHead>
                          <TableHead className="text-right">Saldo</TableHead>
                          <TableHead>Motivo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.cuentas?.map((cuenta, idx) => {
                          const excluido = excludedCuits.has(cuenta.cuit);
                          const ov = paisOverrides[cuenta.cuit] || {};
                          return (
                          <TableRow key={cuenta.cuit || idx} className={excluido ? 'opacity-40 line-through' : (cuenta.needs_review && version === '00300' ? 'bg-red-50' : '')}>
                            <TableCell className="text-center">
                              <button
                                type="button"
                                onClick={() => toggleExcludeCuit(cuenta.cuit)}
                                title={excluido ? 'Volver a incluir' : 'Quitar del archivo'}
                                data-testid={`f8126-toggle-${cuenta.cuit}`}
                                className={`h-6 w-6 inline-flex items-center justify-center rounded ${excluido ? 'bg-slate-200 text-slate-600' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                              >
                                {excluido ? '+' : '×'}
                              </button>
                            </TableCell>
                            <TableCell className="font-medium">{cuenta.razon_social?.substring(0, 30)}</TableCell>
                            <TableCell>{cuenta.cuit}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-cyan-50 text-cyan-700">
                                {cuenta.cant_cvus || 1}
                              </Badge>
                            </TableCell>
                            {version === '00300' && (
                              <TableCell className="text-center">
                                {cuenta.needs_review ? (
                                  <div className="flex items-center gap-1 justify-center">
                                    <select
                                      value={ov.nacionalidad ?? (cuenta.nacionalidad || 'E')}
                                      onChange={(e) => updatePaisOverride(cuenta.cuit, 'nacionalidad', e.target.value)}
                                      className="border border-red-300 rounded px-1 py-0.5 text-xs"
                                      data-testid={`f8126-nac-${cuenta.cuit}`}
                                    >
                                      <option value="N">N</option>
                                      <option value="E">E</option>
                                    </select>
                                    <input
                                      value={ov.pais ?? (cuenta.pais_codigo || '')}
                                      onChange={(e) => updatePaisOverride(cuenta.cuit, 'pais', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                      placeholder="999"
                                      className="border border-red-300 rounded px-1 py-0.5 text-xs w-14"
                                      data-testid={`f8126-pais-${cuenta.cuit}`}
                                    />
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-600">{cuenta.nacionalidad} / {cuenta.pais_codigo}</span>
                                )}
                              </TableCell>
                            )}
                            <TableCell className="text-right text-green-600">{formatMonto(cuenta.total_ingresos)}</TableCell>
                            <TableCell className="text-right text-red-600">{formatMonto(cuenta.total_egresos)}</TableCell>
                            <TableCell className={`text-right font-medium ${cuenta.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {formatMonto(cuenta.saldo)}
                            </TableCell>
                            <TableCell>
                              {cuenta.motivo_inclusion?.map((m) => (
                                <Badge key={m} variant="outline" className="mr-1 text-xs">
                                  {m.includes('Movimientos') ? 'Mov' : 'Saldo'}
                                </Badge>
                              ))}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Comparativa */}
        <TabsContent value="comparativa" className="space-y-6">
          {/* Configuración de Escenarios */}
          <Card className="p-6 bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <GitCompare size={18} />
                Configurar Escenarios
              </h3>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={agregarEscenario}
                disabled={escenarios.length >= 5}
              >
                <Plus size={14} className="mr-1" />
                Agregar
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {escenarios.map((esc, idx) => {
                const color = getColorEscenario(idx);
                return (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border-2 ${color.bg} ${color.border}`}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <Input
                        value={esc.nombre}
                        onChange={(e) => actualizarEscenario(idx, 'nombre', e.target.value)}
                        className={`font-semibold ${color.text} bg-transparent border-none p-0 h-auto text-sm`}
                      />
                      {escenarios.length > 2 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => eliminarEscenario(idx)}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Período</Label>
                        <Input
                          value={esc.periodo}
                          onChange={(e) => actualizarEscenario(idx, 'periodo', e.target.value)}
                          placeholder="YYYYMM"
                          maxLength={6}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Umbral Transacc.</Label>
                        <Input
                          type="number"
                          value={esc.umbral_transacciones}
                          onChange={(e) => actualizarEscenario(idx, 'umbral_transacciones', Number(e.target.value))}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Umbral Saldo</Label>
                        <Input
                          type="number"
                          value={esc.umbral_saldo}
                          onChange={(e) => actualizarEscenario(idx, 'umbral_saldo', Number(e.target.value))}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 flex justify-center gap-3">
              <Button 
                onClick={handleComparativa}
                disabled={loadingComparativa}
                style={{ backgroundColor: '#1e40af' }}
                className="px-8"
              >
                {loadingComparativa ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <ArrowLeftRight size={16} className="mr-2" />
                )}
                Comparar Escenarios
              </Button>
              {comparativaResult && (
                <Button 
                  onClick={handleDescargarComparativaPDF}
                  disabled={loadingComparativa}
                  variant="outline"
                  className="px-6"
                >
                  {loadingComparativa ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <Download size={16} className="mr-2" />
                  )}
                  Descargar PDF
                </Button>
              )}
            </div>
          </Card>

          {/* Resultados de Comparativa */}
          {comparativaResult && (
            <>
              {/* Barra de acciones para comparativa */}
              <div className="flex flex-wrap gap-3 items-center justify-between bg-slate-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    {escenariosSeleccionados.size} escenario(s) seleccionado(s)
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      agregarAlInforme();
                    }}
                    disabled={escenariosSeleccionados.size === 0}
                  >
                    <Plus size={14} className="mr-1" />
                    Agregar al Informe
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      exportarInformeExcel();
                    }}
                    disabled={!comparativaResult && escenariosAcumulados.length === 0}
                  >
                    <FileSpreadsheet size={14} className="mr-1" />
                    Exportar Excel
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      exportarInformePDF();
                    }}
                    disabled={!comparativaResult && escenariosAcumulados.length === 0}
                  >
                    <Download size={14} className="mr-1" />
                    Exportar PDF
                  </Button>
                </div>
              </div>

              {/* Tarjetas de Resumen por Escenario con Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {comparativaResult.escenarios?.map((esc, idx) => {
                  const color = getColorEscenario(idx);
                  const resumen = comparativaResult.resumen_diferencias?.por_escenario?.[esc.nombre] || {};
                  const isSelected = escenariosSeleccionados.has(esc.nombre);
                  return (
                    <Card 
                      key={idx} 
                      className={`p-4 ${color.bg} border-2 ${color.border} cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                      onClick={() => toggleSeleccionEscenario(esc.nombre)}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h4 className={`font-bold ${color.text}`}>{esc.nombre}</h4>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-white'}`}>
                          {isSelected && <Check size={14} className="text-white" />}
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Período:</span>
                          <span className="font-mono font-semibold">{esc.periodo}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Cuentas:</span>
                          <span className="font-bold text-lg">{esc.totales?.total_cuentas || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Exclusivas:</span>
                          <Badge variant="outline" className={color.text}>
                            {resumen.cuentas_exclusivas || 0}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Líneas archivo:</span>
                          <span className="font-mono">{esc.lineas_archivo?.toLocaleString() || 0}</span>
                        </div>
                        <hr className="border-slate-200" />
                        <div className="flex justify-between text-green-700">
                          <span>Ingresos:</span>
                          <span className="font-semibold">{formatMonto(esc.totales?.total_ingresos_sum || 0)}</span>
                        </div>
                        <div className="flex justify-between text-red-700">
                          <span>Egresos:</span>
                          <span className="font-semibold">{formatMonto(esc.totales?.total_egresos_sum || 0)}</span>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Panel de Escenarios Acumulados para el Informe */}
              {escenariosAcumulados.length > 0 && (
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                      <Package size={16} />
                      Informe Acumulado ({escenariosAcumulados.length} escenarios)
                    </h4>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={exportarInformeExcel}
                        className="text-blue-700 border-blue-300"
                      >
                        <FileSpreadsheet size={14} className="mr-1" />
                        Excel
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={exportarInformePDF}
                        className="text-blue-700 border-blue-300"
                      >
                        <Download size={14} className="mr-1" />
                        PDF
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={limpiarInforme}
                        className="text-red-600"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Limpiar
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {escenariosAcumulados.map((esc, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-blue-200 text-sm"
                      >
                        <span className="font-medium">{esc.nombre}</span>
                        <span className="text-slate-500">({esc.periodo})</span>
                        <span className="text-blue-600 font-semibold">{esc.total_cuentas} ctas</span>
                        <button 
                          onClick={() => eliminarDelInforme(idx)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Resumen de Diferencias */}
              <Card className="p-6 bg-white">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle size={18} className="text-amber-500" />
                  Resumen de Diferencias
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Total Cuentas Únicas</p>
                    <p className="text-2xl font-bold">{comparativaResult.resumen_diferencias?.total_cuentas_unicas || 0}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-xs text-green-600">En Todos los Escenarios</p>
                    <p className="text-2xl font-bold text-green-700">{comparativaResult.resumen_diferencias?.cuentas_en_todos || 0}</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-xs text-amber-600">Exclusivas (solo en 1)</p>
                    <p className="text-2xl font-bold text-amber-700">{comparativaResult.resumen_diferencias?.cuentas_exclusivas || 0}</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-xs text-purple-600">Parciales</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {(comparativaResult.resumen_diferencias?.total_cuentas_unicas || 0) - 
                       (comparativaResult.resumen_diferencias?.cuentas_en_todos || 0) -
                       (comparativaResult.resumen_diferencias?.cuentas_exclusivas || 0)}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Tabla de Análisis de Cuentas */}
              <Card className="p-6 bg-white">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Eye size={18} />
                  Análisis de Cuentas (lado a lado)
                </h3>
                
                <div className="max-h-[500px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-white">Razón Social</TableHead>
                        <TableHead>CUIT</TableHead>
                        {comparativaResult.escenarios?.map((esc, idx) => (
                          <TableHead key={idx} className={`text-center ${getColorEscenario(idx).text}`}>
                            {esc.nombre}
                          </TableHead>
                        ))}
                        <TableHead className="text-center">Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparativaResult.analisis_cuentas?.map((cuenta, idx) => (
                        <TableRow key={idx} className={cuenta.exclusiva ? 'bg-amber-50/50' : ''}>
                          <TableCell className="sticky left-0 bg-white font-medium max-w-[200px] truncate">
                            {cuenta.razon_social?.substring(0, 30)}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{cuenta.cuit}</TableCell>
                          {comparativaResult.escenarios?.map((esc, escIdx) => {
                            const presente = cuenta.presencia?.[esc.nombre];
                            const datos = cuenta.datos_por_escenario?.[esc.nombre];
                            const color = getColorEscenario(escIdx);
                            return (
                              <TableCell key={escIdx} className="text-center">
                                {presente ? (
                                  <div className={`p-2 rounded ${color.bg}`}>
                                    <p className="text-xs font-semibold">{formatMonto(datos?.total_movimientos || 0)}</p>
                                    <p className={`text-xs ${datos?.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      Saldo: {formatMonto(datos?.saldo || 0)}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-center">
                            {cuenta.en_todos ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle size={12} className="mr-1" />
                                Todos
                              </Badge>
                            ) : cuenta.exclusiva ? (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                <AlertTriangle size={12} className="mr-1" />
                                Exclusiva
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                Parcial
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Tab: Múltiples Períodos */}
        <TabsContent value="multiple" className="space-y-6">
          <Card className="p-6 bg-white">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Package size={18} />
              Generar Múltiples Períodos
            </h3>
            
            <div className="space-y-4 max-w-xl">
              <div>
                <Label>Períodos (separados por coma)</Label>
                <Input 
                  value={periodosMultiples}
                  onChange={(e) => setPeriodosMultiples(e.target.value)}
                  placeholder="202401, 202402, 202403, ..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  Formato YYYYMM. Ejemplo: 202401, 202402, 202403
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Umbral Transacciones</Label>
                  <Input 
                    type="number"
                    value={umbralTransacciones} 
                    onChange={(e) => setUmbralTransacciones(Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Umbral Saldo</Label>
                  <Input 
                    type="number"
                    value={umbralSaldo} 
                    onChange={(e) => setUmbralSaldo(Number(e.target.value))}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <input 
                  type="checkbox" 
                  id="sinMovimientosMultiple"
                  checked={generarSinMovimientos}
                  onChange={(e) => setGenerarSinMovimientos(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <Label htmlFor="sinMovimientosMultiple" className="text-sm cursor-pointer">
                  Generar sin movimientos (solo cabecera si no hay datos)
                </Label>
              </div>
              
              <Button 
                onClick={handleGenerarMultiple}
                disabled={loading || !periodosMultiples}
                style={{ backgroundColor: '#1e40af' }}
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Package size={16} className="mr-2" />}
                Generar y Descargar ZIP
              </Button>
            </div>
          </Card>

          {/* Resumen de períodos existentes */}
          <Card className="p-6 bg-white">
            <h3 className="font-semibold mb-4">Períodos con Reportes</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {resumen.periodos?.map((p) => (
                <div key={p.periodo} className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="font-mono font-bold">{p.periodo}</p>
                  <p className="text-xs text-slate-500">{p.cantidad_reportes} reportes</p>
                  <p className="text-xs text-slate-500">{p.total_cuentas} cuentas</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Historial */}
        <TabsContent value="historial">
          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <History size={18} />
                Historial de Reportes
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={forzarTA}
                disabled={taBusy}
                data-testid="f8126-forzar-ta-btn"
                title="Renueva el token de ARCA (úsalo tras cambiar delegaciones/autorizaciones en ARCA, antes de reintentar)"
                className="gap-2"
              >
                <RefreshCw size={14} className={taBusy ? 'animate-spin' : ''} />
                {taBusy ? 'Renovando…' : 'Forzar token ARCA'}
              </Button>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead>Archivo</TableHead>
                  <TableHead>Cuentas</TableHead>
                  <TableHead>Registros</TableHead>
                  <TableHead>Generado</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>ARCA</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historial.map((rep) => (
                  <TableRow key={rep.id}>
                    <TableCell className="font-mono">{rep.periodo}</TableCell>
                    <TableCell className="font-mono text-sm">{rep.nombre_archivo}</TableCell>
                    <TableCell>{rep.estadisticas?.total_cuentas || '-'}</TableCell>
                    <TableCell>{rep.estadisticas?.total_registros || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {new Date(rep.created_at).toLocaleString('es-AR')}
                    </TableCell>
                    <TableCell className="text-sm">{rep.generado_por}</TableCell>
                    <TableCell>
                      {(() => {
                        const p = presMap[rep.id];
                        const busy = presBusy === rep.id || (p && presBusy === p.id);
                        if (p && p.estado === 'PRESENTADA') {
                          return (
                            <div className="flex items-center gap-1">
                              <Badge className="bg-green-100 text-green-700 border-0 text-[10px]">
                                <CheckCircle size={11} className="mr-1" />Nº {p.numeroTransaccion || 'OK'}
                              </Badge>
                              <Button size="sm" variant="ghost" title="Consultar estado en ARCA"
                                onClick={() => consultarArca(p.id)} disabled={busy}
                                data-testid={`f8126-arca-estado-${rep.id}`}>
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                              </Button>
                            </div>
                          );
                        }
                        return (
                          <div className="flex items-center gap-1">
                            {p && p.estado === 'ERROR' && (
                              <Badge className="bg-red-100 text-red-700 border-0 text-[10px]" title={p.error || 'Error'}>Error</Badge>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-teal-700 border-teal-300"
                              onClick={() => presentarArca(rep)} disabled={busy}
                              data-testid={`f8126-arca-presentar-${rep.id}`}>
                              {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />}
                              {p && p.estado === 'ERROR' ? 'Reintentar' : 'Presentar en ARCA'}
                            </Button>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDescargar(rep.id, rep.nombre_archivo)}
                        >
                          <Download size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => handleEliminar(rep.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tab: Presentaciones (Tablero de Control) */}
        <TabsContent value="presentaciones" className="space-y-6">
          <PresentacionesTab />
        </TabsContent>

        {/* Tab: Configuración */}
        <TabsContent value="config">
          <Card className="p-6 bg-white max-w-xl">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Settings size={18} />
              Configuración del Informante
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label>CUIT Informante</Label>
                <Input 
                  value={config.cuit_informante}
                  onChange={(e) => setConfig({...config, cuit_informante: e.target.value})}
                  placeholder="33718330179"
                />
              </div>
              
              <div>
                <Label>Denominación</Label>
                <Input 
                  value={config.denominacion_informante}
                  onChange={(e) => setConfig({...config, denominacion_informante: e.target.value})}
                  placeholder="WAYA S.A."
                />
              </div>
              
              <div>
                <Label>Código Concepto</Label>
                <Input 
                  value={config.codigo_concepto}
                  onChange={(e) => setConfig({...config, codigo_concepto: e.target.value})}
                  placeholder="812"
                />
              </div>
              
              <div>
                <Label>Versión Aplicativo</Label>
                <Input 
                  value={config.version_aplicativo}
                  onChange={(e) => setConfig({...config, version_aplicativo: e.target.value})}
                  placeholder="00200"
                />
              </div>
              
              <Button onClick={handleSaveConfig} style={{ backgroundColor: '#1e40af' }}>
                Guardar Configuración
              </Button>
            </div>
          </Card>

          {/* Sincronización de Padrón */}
          <Card className="p-6 bg-white max-w-xl mt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Database size={18} />
              Sincronización de Datos
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Sincroniza el padrón interno desde MySQL. Esto es necesario después de un deploy 
              o cuando se agregan nuevos clientes en la base de datos de origen.
            </p>
            <Button 
              onClick={handleSincronizarPadron} 
              variant="outline"
              disabled={loading}
              className="w-full"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              Sincronizar Padrón desde MySQL
            </Button>
          </Card>

          {/* Exportar/Importar Padrón */}
          <Card className="p-6 bg-white max-w-xl mt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <ArrowLeftRight size={18} />
              Exportar / Importar Padrón
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Usa estas opciones para migrar el padrón entre ambientes (preview → producción).
              La importación valida CVUs, normaliza a 22 caracteres y no duplica datos existentes.
            </p>
            
            <div className="space-y-3">
              <Button 
                onClick={handleExportarPadron} 
                variant="outline"
                disabled={loading}
                className="w-full"
              >
                <Download size={16} className="mr-2" />
                Exportar Padrón a JSON
              </Button>
              
              <div className="relative">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportarPadron}
                  disabled={loading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  id="import-padron-file"
                />
                <Button 
                  variant="outline"
                  disabled={loading}
                  className="w-full pointer-events-none"
                >
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
                  Importar Padrón desde JSON
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
